import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  InputAdornment,
  useTheme
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Agent } from '../../types/agent';
import { WhatsAppConfig } from '../../utils/agentApi';
import { formatPhoneNumber, cleanPhoneNumber, isValidPhoneNumber } from '../../utils/phoneMask';
import { useTranslation } from 'react-i18next';

interface WhatsAppConfigModalProps {
  open: boolean;
  onClose: () => void;
  agent: Agent | null;
  onSave: (config: WhatsAppConfig) => Promise<void>;
  isLoading?: boolean;
}

const WhatsAppConfigModal: React.FC<WhatsAppConfigModalProps> = ({
  open,
  onClose,
  agent,
  onSave,
  isLoading = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [config, setConfig] = useState<WhatsAppConfig>({
    exigirNome: false,
    exigirNomeCompleto: false,
    naoAcumularMensagens: false,
    shouldBlockAI: false,
    numbersToHomologate: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [homologationNumbers, setHomologationNumbers] = useState<string[]>([]);
  const [newNumber, setNewNumber] = useState('');

  const formatNumberForDisplay = (backendNumber: string): string => {
    const cleaned = backendNumber.trim();
    // Remove country code 55 if present for display
    if (cleaned.startsWith('55') && cleaned.length > 11) {
      return formatPhoneNumber(cleaned.substring(2));
    }
    return formatPhoneNumber(cleaned);
  };

  useEffect(() => {
    if (agent && open) {
      const numbersString = agent.numbersToHomologate || '';
      const backendNumbers = numbersString ? numbersString.split(';').filter(num => num.trim()) : [];

      // Convert backend numbers (with 55) to display format (without 55)
      const displayNumbers = backendNumbers.map(formatNumberForDisplay);

      setConfig({
        exigirNome: agent.exigirNome || false,
        exigirNomeCompleto: agent.exigirNomeCompleto || false,
        naoAcumularMensagens: agent.naoAcumularMensagens ?? false,
        shouldBlockAI: agent.shouldBlockAI || false,
        numbersToHomologate: numbersString
      });
      setHomologationNumbers(displayNumbers);
      setNewNumber('');
      setError(null);
    }
  }, [agent, open]);

  const handleInputChange = (field: keyof WhatsAppConfig, value: boolean | string) => {
    setConfig(prev => {
      const newConfig = {
        ...prev,
        [field]: value
      };

      // Clear numbersToHomologate when shouldBlockAI is disabled
      if (field === 'shouldBlockAI' && !value) {
        newConfig.numbersToHomologate = '';
        setHomologationNumbers([]);
      }

      return newConfig;
    });
  };

  const formatNumberForBackend = (phoneNumber: string): string => {
    const cleaned = cleanPhoneNumber(phoneNumber);
    // Add country code 55 if not already present
    if (cleaned.length >= 10 && !cleaned.startsWith('55')) {
      return `55${cleaned}`;
    }
    return cleaned;
  };

  const addNumber = () => {
    if (newNumber.trim() && isValidPhoneNumber(newNumber)) {
      const formattedForDisplay = newNumber.trim();
      const existingNumbers = homologationNumbers.map(num => cleanPhoneNumber(num));
      const newCleanNumber = cleanPhoneNumber(formattedForDisplay);

      // Check if number already exists (comparing clean versions)
      if (!existingNumbers.includes(newCleanNumber)) {
        const updatedNumbers = [...homologationNumbers, formattedForDisplay];
        setHomologationNumbers(updatedNumbers);

        // Format numbers for backend (clean + add 55)
        const backendFormattedNumbers = updatedNumbers.map(formatNumberForBackend);
        setConfig(prev => ({
          ...prev,
          numbersToHomologate: backendFormattedNumbers.join(';')
        }));
        setNewNumber('');
      }
    }
  };

  const removeNumber = (numberToRemove: string) => {
    const updatedNumbers = homologationNumbers.filter(num => num !== numberToRemove);
    setHomologationNumbers(updatedNumbers);

    // Format numbers for backend (clean + add 55)
    const backendFormattedNumbers = updatedNumbers.map(formatNumberForBackend);
    setConfig(prev => ({
      ...prev,
      numbersToHomologate: backendFormattedNumbers.join(';')
    }));
  };

  const handleNewNumberKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addNumber();
    }
  };

  const handleSave = async () => {
    try {
      setError(null);
      await onSave(config);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('agents.whatsapp.configModal.saveError'));
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('agents.whatsapp.configModal.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('agents.whatsapp.configModal.subtitle')}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={config.exigirNome}
                onChange={(e) => handleInputChange('exigirNome', e.target.checked)}
                disabled={isLoading}
              />
            }
            label={t('agents.whatsapp.configModal.requireName')}
          />

          <FormControlLabel
            control={
              <Switch
                checked={config.exigirNomeCompleto}
                onChange={(e) => handleInputChange('exigirNomeCompleto', e.target.checked)}
                disabled={isLoading}
              />
            }
            label={t('agents.whatsapp.configModal.requireFullName')}
          />

          <FormControlLabel
            control={
              <Switch
                checked={config.naoAcumularMensagens}
                onChange={(e) => handleInputChange('naoAcumularMensagens', e.target.checked)}
                disabled={isLoading}
              />
            }
            label={t('agents.whatsapp.configModal.doNotAccumulateMessages')}
          />

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={config.shouldBlockAI}
                  onChange={(e) => handleInputChange('shouldBlockAI', e.target.checked)}
                  disabled={isLoading}
                />
              }
              label={t('agents.whatsapp.configModal.blockAI')}
            />
            <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
              {t('agents.whatsapp.configModal.blockAIDescription')}
            </Alert>
          </Box>

          {config.shouldBlockAI && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t('agents.whatsapp.configModal.homologationNumbers')}
              </Typography>

              <TextField
                fullWidth
                label={t('agents.whatsapp.configModal.addNumber')}
                value={newNumber}
                onChange={(e) => setNewNumber(formatPhoneNumber(e.target.value))}
                onKeyPress={handleNewNumberKeyPress}
                placeholder={t('agents.whatsapp.configModal.addNumberPlaceholder')}
                disabled={isLoading}
                error={newNumber.length > 0 && !isValidPhoneNumber(newNumber)}
                helperText={
                  newNumber.length > 0 && !isValidPhoneNumber(newNumber)
                    ? t('agents.whatsapp.configModal.invalidPhone')
                    : t('agents.whatsapp.configModal.phoneHelper')
                }
                inputProps={{
                  maxLength: 15, // (XX) XXXXX-XXXX format
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={addNumber}
                        disabled={isLoading || !newNumber.trim() || !isValidPhoneNumber(newNumber)}
                        size="small"
                      >
                        <AddIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              {homologationNumbers.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {homologationNumbers.map((number, index) => (
                    <Chip
                      key={index}
                      label={number}
                      onDelete={() => removeNumber(number)}
                      deleteIcon={<DeleteIcon />}
                      variant="outlined"
                      disabled={isLoading}
                    />
                  ))}
                </Box>
              )}

              {homologationNumbers.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('agents.whatsapp.configModal.noNumbersAdded')}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          color="inherit"
        >
          {t('agents.whatsapp.configModal.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? t('agents.whatsapp.configModal.saving') : t('agents.whatsapp.configModal.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsAppConfigModal;
